import { Box, Button, CircularProgress } from '@mui/material';
import { blue, grey } from '@mui/material/colors';
import { ConfigurationContext } from 'components/provider/OrginazationContext';
import { forwardRef, useContext, useRef } from 'react';
import { BiPrinter } from 'react-icons/bi';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';
import { useGetRoutineCourseDetailById } from 'service/admin.query';

const RoutineDetailSchedule = forwardRef<HTMLDivElement, { routineId: string }>(
  ({ routineId }, ref) => {
    const { data: routineCourseDetail, isLoading } = useGetRoutineCourseDetailById(routineId, !!routineId);
    const { appConfig } = useContext(ConfigurationContext);

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    if (!routineCourseDetail) return null;

    // Helper to extract unique dates and sort them
    const allDates = Array.from(new Set(
        routineCourseDetail.routine_sessions.flatMap((session:any) => 
          session.daily_sessions.map((ds:any) => ds.routine_date)
        )
      )).sort();
console.log(appConfig);

    return (
      <div ref={ref} style={{ color: '#333', fontFamily: 'Arial, sans-serif' }}>
        <div id="schedule" style={{ backgroundColor: 'white', padding: '20px' }}>
          
          {/* HEADER SECTION */}
          <div id="header" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '24px' }}>{appConfig?.title || "Nepal Administrative Staff College"}</h1>
            <h3 style={{ margin: '5px 0', fontWeight: 'normal' }}>Training Management Information System</h3>
            <h5 style={{ margin: '5px 0', fontWeight: 'normal' }}>{appConfig?.address}</h5>

            <hr />
            <h4 style={{ margin: '5px 0', fontWeight: 'normal' }}>Training Programme on</h4>

            <h2 style={{ color: blue[800], margin: '10px 0' }}>{routineCourseDetail.training_name}</h2>
            <h4 style={{ margin: '5px 0' }}>Batch: {routineCourseDetail.batch_name} ({routineCourseDetail.hall} Hall)</h4>
            <p style={{ fontWeight: 'bold' }}>
              Duration: {routineCourseDetail.start_date} – {routineCourseDetail.end_date}
            </p>
            <h3 style={{ textDecoration: 'underline', marginTop: '20px' }}>PROGRAMME SCHEDULE</h3>
          </div>

          {/* COORDINATORS SECTION */}
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', border: `1px solid ${grey[300]}`, padding: '10px' }}>
            <div>
              <strong>Course Coordinators:</strong> {routineCourseDetail.coordinators.join(', ')}
            </div>
            <div>
              <strong>Module Coordinators:</strong> {routineCourseDetail.module_coordinators.join(', ')}
            </div>
          </div>

          {/* ROUTINE TABLE */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
            <thead>
              <tr style={{ backgroundColor: grey[100] }}>
                <th style={tableCellStyle}>Date</th>
                {routineCourseDetail.routine_sessions.map((session:any,index:number) => (
                  <th key={index} style={tableCellStyle}>
                    {session.session_name} <br />
                    <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                      ({session.start_time.substring(0,5)} - {session.end_time.substring(0,5)})
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allDates.map((date:any) => (
                <tr key={date}>
                  <td style={{ ...tableCellStyle, fontWeight: 'bold', width: '120px' }}>{date}</td>
                  {routineCourseDetail.routine_sessions.map((session:any) => {
                    const sessionData = session.daily_sessions.find((ds:any) => ds.routine_date === date);
                    return (
                      <td key={session.id} style={tableCellStyle}>
                        {sessionData ? (
                          <>
                            <div style={{ fontWeight: '600', fontSize: '13px' }}>{sessionData.course_topic}</div>
                            <div style={{ fontSize: '12px', color: grey[700], marginTop: '4px' }}>
                              <em>Resource Person: {sessionData.lecturers.join(', ')}</em>
                            </div>
                          </>
                        ) : (
                          <span style={{ color: grey[400] }}>-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* COURSE MODULES LIST */}
          <div style={{ marginTop: '40px', pageBreakBefore: 'always' }}>
            <h3 style={{ borderBottom: `2px solid ${blue[500]}`, paddingBottom: '5px' }}>Course Modules & Topics</h3>
            {routineCourseDetail.courses.map((course:any) => (
              <div key={course.id} style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '10px 0 5px 0', color: blue[700] }}>
                  {course.code}: {course.title} ({course.duration_hr} hrs)
                </h4>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{textAlign: 'left', fontSize: '12px', borderBottom: `1px solid ${grey[300]}`}}>
                            <th style={{padding: '5px'}}>Topic</th>
                            <th style={{padding: '5px'}}>Methodology</th>
                            <th style={{padding: '5px'}}>Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {course.topics.map((topic:any) => (
                        <tr key={topic.topic_id} style={{ borderBottom: `1px solid ${grey[100]}`, fontSize: '13px' }}>
                            <td style={{ padding: '5px' }}>{topic.topic_title}</td>
                            <td style={{ padding: '5px' }}>{topic.methodology}</td>
                            <td style={{ padding: '5px' }}>{topic.topic_duration_hr} hr</td>
                        </tr>
                        ))}
                    </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

const tableCellStyle = {
  border: `1px solid ${grey[400]}`,
  padding: '10px',
  textAlign: 'center' as const,
};

const PrintableSchedule = ({ routineId }: { routineId: any }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <div>
      <ReactToPrint
        content={() => ref.current}
        pageStyle={`
            @page {
                background: white;
                margin: 15mm;  
            }
            @media print {
                body { -webkit-print-color-adjust: exact; }
                table { page-break-inside: auto; }
                tr { page-break-inside: avoid; page-break-after: auto; }
            }
        `}>
        <PrintContextConsumer>
          {({ handlePrint }) => (
            <div style={{ margin: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="primary" onClick={handlePrint} startIcon={<BiPrinter />}>
                Print Schedule
              </Button>
            </div>
          )}
        </PrintContextConsumer>
      </ReactToPrint>

      <RoutineDetailSchedule ref={ref} routineId={routineId} />
    </div>
  );
};

export default PrintableSchedule;
RoutineDetailSchedule.displayName = 'RoutineDetailSchedule';